import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Bell, Check, ShoppingCart, Package, CheckCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      toast.error('Erro ao carregar notificações');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`);
      fetchNotifications();
    } catch (error) {
      toast.error('Erro ao marcar notificação como lida');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`);
      toast.success('Todas as notificações marcadas como lidas');
      fetchNotifications();
    } catch (error) {
      toast.error('Erro ao marcar notificações como lidas');
    }
  };

  const unreadCount = notifications.filter(n => !n.lida).length;

  const getIcon = (tipo) => {
    switch (tipo) {
      case 'novo_pedido':
        return <ShoppingCart className="h-5 w-5 text-primary" />;
      case 'status_mudou':
        return <Package className="h-5 w-5 text-blue-500" />;
      default:
        return <Bell className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold font-manrope tracking-tight">Notificações</h1>
            <p className="text-muted-foreground mt-1">Central de avisos e atualizações</p>
          </div>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              onClick={markAllAsRead}
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="h-4 w-4 mr-2" />
              Marcar todas como lidas ({unreadCount})
            </Button>
          )}
        </div>

        <div className="space-y-3">
          {notifications.map((notif) => (
            <Card 
              key={notif.id} 
              className={`border-border hover:border-primary/50 transition-colors cursor-pointer ${!notif.lida ? 'bg-primary/5' : ''}`}
              onClick={() => {
                if (!notif.lida) markAsRead(notif.id);
                if (notif.order_id) navigate(`/orders`);
              }}
              data-testid={`notification-${notif.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    {getIcon(notif.tipo)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h3 className="font-semibold">{notif.titulo}</h3>
                      {!notif.lida && (
                        <Badge variant="default" className="bg-primary">Nova</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{notif.mensagem}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(notif.createdAt).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  {!notif.lida && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notif.id);
                      }}
                      data-testid={`mark-read-${notif.id}`}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {notifications.length === 0 && (
          <Card className="border-border">
            <CardContent className="py-16 text-center">
              <Bell className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Nenhuma notificação</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
